import React, { Component } from "react";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { toastr } from "react-redux-toastr";
import InfiniteScroll from "react-infinite-scroll-component";
import { Button, ButtonGroup } from "react-bootstrap";
import { FiGrid, FiList, FiPlus } from "react-icons/fi";
import { TabletOrDesktop } from "utils/responsive";
import moment from "moment";
import { DateRangePicker } from "react-dates";
import "react-dates/lib/css/_datepicker.css";
import "react-dates/initialize";
import Layout from "components/Layout";
import Loading from "components/Loading";
import Modal from "components/Modal";
import ShiftExchangesList from "components/ShiftExchanges/ShiftExchangesList";
import ShiftExchangeCreateModal from "components/ShiftExchanges/ShiftExchangeCreateModal";
import EmployeeSelectSearch from "components/EmployeeSelectSearch/EmployeeSelectSearch";
import { hideModal, showModal } from "actions/modalActions";
import {
	exchangeStatus,
	planAssignmentExchangesService,
} from "services/planAssignmentExchangesService";
import { errorFormating } from "utils/utils";

class ShiftExchangesManagement extends Component {
	constructor(props) {
		super(props);
		this.state = {
			exchanges: [],
			isLoading: false,
			page: 0,
			page_size: 20,
			hasMore: true,
			isListView: false,
			employee_id: -1,
			start_from: null,
			start_to: null,
			focusedInput: null,
		};
		this.requestId = 0;
	}

	componentDidMount() {
		this.getExchanges();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.mode !== this.props.mode) {
			this.reloadExchanges();
		}
	}

	getStatuses() {
		return this.props.mode === "pending"
			? [exchangeStatus.accepted]
			: [
					exchangeStatus.approved,
					exchangeStatus.denied,
					exchangeStatus.canceled,
				];
	}

	getExchanges = async () => {
		const {
			page,
			page_size,
			exchanges,
			employee_id,
			start_from,
			start_to,
		} = this.state;
		const requestId = ++this.requestId;
		this.setState({ isLoading: page === 0 });

		try {
			const response = await planAssignmentExchangesService.getAll({
				statuses: this.getStatuses(),
				start_from,
				start_to,
				page,
				page_size,
			});
			if (requestId !== this.requestId) return;

			let newItems = response.data || (Array.isArray(response) ? response : []);
			if (Number(employee_id) > 0) {
				newItems = newItems.filter(
					(exchange) =>
						Number(exchange.requester_employee?.id) === Number(employee_id) ||
						Number(exchange.target_employee?.id) === Number(employee_id)
				);
			}
			const allItems = page === 0 ? newItems : exchanges.concat(newItems);
			const totalItems = response.pagination?.total_items || response.total || 0;
			const hasMore =
				Number(employee_id) <= 0 &&
				newItems.length > 0 &&
				(totalItems > 0
					? allItems.length < totalItems
					: newItems.length === page_size);

			this.setState({ exchanges: allItems, isLoading: false, hasMore });
		} catch (error) {
			if (requestId !== this.requestId) return;
			console.error("Error fetching shift exchanges:", error);
			this.setState({ isLoading: false, hasMore: false });
		}
	};

	reloadExchanges = () => {
		this.setState({ exchanges: [], page: 0, hasMore: true }, this.getExchanges);
	};

	fetchMoreData = () => {
		this.setState((state) => ({ page: state.page + 1 }), this.getExchanges);
	};

	handleEmployeeFilterChange = (id) => {
		this.setState(
			{
				employee_id: id ? Number(id) : -1,
				exchanges: [],
				page: 0,
				hasMore: true,
			},
			this.getExchanges
		);
	};

	handleDatesChange = ({ startDate, endDate }) => {
		this.setState(
			{
				start_from: startDate ? startDate.format("YYYY-MM-DD") : null,
				start_to: endDate ? endDate.format("YYYY-MM-DD") : null,
				exchanges: [],
				page: 0,
				hasMore: true,
			},
			this.getExchanges
		);
	};

	openAlertModal = (message, title) => {
		this.props.showModal(
			{
				open: true,
				title: title || this.props.t("common.error"),
				style: { width: "400px" },
				message,
				closeModal: this.props.hideModal,
			},
			"alert"
		);
	};

	openNewExchangeModal = () => {
		this.props.showModal(
			{
				open: true,
				title: this.props.t("shiftExchanges.newRequest"),
				style: { width: "680px" },
				content: (
					<ShiftExchangeCreateModal
						handleClose={this.props.hideModal}
						reloadExchanges={this.reloadExchanges}
					/>
				),
				closeModal: this.props.hideModal,
			},
			"edit"
		);
	};

	openViewExchangeModal = (exchange) => {
		this.props.showModal(
			{
				open: true,
				title: this.props.t("shiftExchanges.viewRequest"),
				style: { width: "680px" },
				content: (
					<ShiftExchangeCreateModal
						exchange={exchange}
						disabled={true}
						handleClose={this.props.hideModal}
						reloadExchanges={this.reloadExchanges}
					/>
				),
				closeModal: this.props.hideModal,
			},
			"edit"
		);
	};

	updateExchangeStatus = async (exchange, status) => {
		const response = await planAssignmentExchangesService.update({
			...exchange,
			status,
			requester_plan_id:
				exchange.requester_plan?.assignments?.[0]?.id || exchange.requester_plan_id,
			target_plan_id:
				exchange.target_plan?.assignments?.[0]?.id || exchange.target_plan_id,
		});

		if (response.ok) {
			this.props.hideModal();
			toastr.success(
				this.props.t("common.success"),
				this.props.t("common.changesSaved")
			);
			this.reloadExchanges();
			return;
		}

		const error = errorFormating(response);
		this.props.hideModal();
		this.openAlertModal(error.general, this.props.t("shiftExchanges.errorManage"));
	};

	cancelExchange = (exchange) => {
		this.props.showModal(
			{
				open: true,
				title: this.props.t("shiftExchanges.cancelRequest"),
				style: { width: "400px" },
				message: this.props.t("shiftExchanges.cancelRequestConfirm"),
				confirmText: this.props.t("shiftExchanges.cancelRequest"),
				confirmAction: () =>
					this.updateExchangeStatus(exchange, exchangeStatus.canceled),
				closeModal: this.props.hideModal,
			},
			"confirm"
		);
	};

	reopenExchange = (exchange) => {
		this.props.showModal(
			{
				open: true,
				title: this.props.t("shiftExchanges.reopenRequest"),
				style: { width: "400px" },
				message: this.props.t("shiftExchanges.reopenRequestConfirm"),
				confirmAction: () =>
					this.updateExchangeStatus(exchange, exchangeStatus.pending),
				closeModal: this.props.hideModal,
			},
			"confirm"
		);
	};

	renderContent() {
		if (this.state.isLoading && this.state.page === 0) return <Loading />;

		return (
			<InfiniteScroll
				dataLength={this.state.exchanges.length}
				next={this.fetchMoreData}
				hasMore={this.state.hasMore}
				loader={<div className="text-center p10">Loading...</div>}
			>
				<ShiftExchangesList
					exchanges={this.state.exchanges}
					isListView={this.props.mode === "managed" && this.state.isListView}
					showApprovalActions={this.props.mode === "pending"}
					showManagedActions={this.props.mode === "managed"}
					openViewExchangeModal={this.openViewExchangeModal}
					cancelExchange={this.cancelExchange}
					reopenExchange={this.reopenExchange}
					openAlertModal={this.openAlertModal}
					reloadExchanges={this.reloadExchanges}
				/>
			</InfiniteScroll>
		);
	}

	render() {
		const title =
			this.props.mode === "pending"
				? this.props.t("shiftExchanges.approveRequests")
				: this.props.t("shiftExchanges.managedRequests");

		return (
			<>
				<Layout className="page-absences page-shift-exchanges" context="shift-exchanges">
					<div className="heading">
						<h1 className="title">{title}</h1>
						<div className="heading-actions">
							<EmployeeSelectSearch
								className="form-select-user mr10"
								controlId="employee_id"
								multiple={false}
								value={this.state.employee_id === -1 ? null : this.state.employee_id}
								onChange={this.handleEmployeeFilterChange}
								fetchOptions={{ enabled: true }}
								placeholder={this.props.t("common.searchEmployee")}
								allLabel={this.props.t("shiftExchanges.allEmployees")}
							/>
							<DateRangePicker
								startDateId="shiftExchangeStartDate"
								endDateId="shiftExchangeEndDate"
								startDate={this.state.start_from ? moment(this.state.start_from) : null}
								endDate={this.state.start_to ? moment(this.state.start_to) : null}
								startDatePlaceholderText={this.props.t("common.from")}
								endDatePlaceholderText={this.props.t("common.to")}
								onDatesChange={this.handleDatesChange}
								focusedInput={this.state.focusedInput}
								onFocusChange={(focusedInput) => this.setState({ focusedInput })}
								hideKeyboardShortcutsPanel={true}
								isOutsideRange={() => false}
								minimumNights={0}
							/>
							{this.props.mode === "managed" && (
								<TabletOrDesktop>
									<ButtonGroup aria-label={this.props.t("shiftExchanges.viewMode")}>
										<Button
											variant={this.state.isListView ? "primary" : "default"}
											onClick={() => this.setState({ isListView: true })}
											title={this.props.t("shiftExchanges.listView")}
										>
											<FiList className="icon" />
										</Button>
										<Button
											variant={!this.state.isListView ? "primary" : "default"}
											onClick={() => this.setState({ isListView: false })}
											title={this.props.t("shiftExchanges.cardView")}
										>
											<FiGrid className="icon" />
										</Button>
									</ButtonGroup>
								</TabletOrDesktop>
							)}
							<button
								type="button"
								className="btn btn-new"
								title={this.props.t("shiftExchanges.newRequest")}
								aria-label={this.props.t("shiftExchanges.newRequest")}
								onClick={this.openNewExchangeModal}
							>
								<FiPlus />
							</button>
						</div>
					</div>
					{this.renderContent()}
				</Layout>
				<Modal hideModal={this.props.hideModal} />
			</>
		);
	}
}

const mapDispatchToProps = (dispatch) => ({
	hideModal: () => dispatch(hideModal()),
	showModal: (modalProps, modalType) =>
		dispatch(showModal({ modalProps, modalType })),
});

export default connect(
	null,
	mapDispatchToProps
)(withTranslation()(ShiftExchangesManagement));
